/**
 * <s-background-music> — optional background music with a welcome track and playlist.
 *
 * - Off until the visitor chooses: tapping the music button, or pressing Enter on the
 *   homepage intro curtain, starts it. A visitor who turned music off stays off.
 * - Enter plays the welcome track (if set), fading in slowly; when it ends the main
 *   playlist fades in. The playlist can shuffle and keeps going track after track.
 * - The choice, current track and position carry across pages. Browsers only allow
 *   sound after an interaction, so on a new page music resumes on the first tap,
 *   click or key press if autoplay is blocked.
 *
 * Commands:
 *   --toggle — turn music on or off.
 *
 * Attributes:
 *   volume  — 0–100, default 30.
 *   fade    — fade-in length in seconds, default 4.
 *   shuffle — present to shuffle the playlist.
 *   intro   — welcome track URL.
 * Children:
 *   <script type="application/json" data-tracks> — array of playlist URLs.
 */
const PREF_KEY = 'holbc-music';
const STATE_KEY = 'holbc-music-state';

class SBackgroundMusic extends HTMLElement {
  /** @type {HTMLAudioElement | null} */
  #audio = null;
  /** @type {HTMLButtonElement | null} */
  #button = null;
  /** @type {AbortController | null} */
  #ac = null;
  /** @type {AbortController | null} */
  #resumeAc = null;
  /** @type {string[]} */
  #tracks = [];
  /** @type {number[]} */
  #order = [];
  #position = 0;
  #inIntro = false;
  #volume = 0.3;
  #fadeMs = 4000;
  #fadeFrame = 0;

  connectedCallback() {
    this.#audio = this.querySelector('audio');
    this.#button = this.querySelector('button');
    if (!this.#audio || !this.#button) return;

    this.#ac = new AbortController();
    const { signal } = this.#ac;

    this.#volume = Math.min(Math.max(Number(this.getAttribute('volume')) || 30, 0), 100) / 100;
    this.#fadeMs = Math.max(Number(this.getAttribute('fade')) || 4, 0) * 1000;
    try {
      const json = this.querySelector('script[data-tracks]')?.textContent || '[]';
      this.#tracks = JSON.parse(json).filter((/** @type {unknown} */ url) => typeof url === 'string' && url);
    } catch {
      this.#tracks = [];
    }

    this.addEventListener('command', (event) => {
      if (/** @type {any} */ (event).command === '--toggle') this.#toggle();
    }, { signal });
    this.#audio.addEventListener('play', () => this.#render(true), { signal });
    this.#audio.addEventListener('pause', () => this.#render(false), { signal });
    this.#audio.addEventListener('ended', () => this.#next(), { signal });
    window.addEventListener('pagehide', () => this.#saveState(), { signal });

    // Homepage curtain: Enter is a real tap, so the browser allows sound to start.
    document.addEventListener('intro-gate:enter', () => {
      if (this.#read(localStorage, PREF_KEY) === 'off') return;
      this.#write(localStorage, PREF_KEY, 'on');
      this.#resumeAc?.abort();
      const intro = this.getAttribute('intro');
      if (intro) this.#startIntro(intro);
      else this.#startPlaylist(true);
    }, { signal });

    this.#restoreState();
    if (this.#read(localStorage, PREF_KEY) === 'on') this.#resume();
  }

  disconnectedCallback() {
    this.#saveState();
    this.#ac?.abort();
    this.#resumeAc?.abort();
    cancelAnimationFrame(this.#fadeFrame);
  }

  #toggle() {
    if (!this.#audio) return;
    if (this.#audio.paused) {
      this.#write(localStorage, PREF_KEY, 'on');
      this.#resumeAc?.abort();
      if (!this.#audio.getAttribute('src')) this.#startPlaylist(true);
      else this.#playWithFade();
    } else {
      this.#write(localStorage, PREF_KEY, 'off');
      this.#resumeAc?.abort();
      this.#audio.pause();
    }
  }

  /** @param {string} url */
  #startIntro(url) {
    if (!this.#audio) return;
    this.#inIntro = true;
    this.#audio.loop = false;
    this.#audio.src = url;
    this.#playWithFade();
  }

  /** @param {boolean} fresh - Start from the first track of a new order. */
  #startPlaylist(fresh) {
    if (!this.#audio || this.#tracks.length === 0) return;
    this.#inIntro = false;
    if (fresh || this.#order.length !== this.#tracks.length) {
      this.#order = this.#makeOrder();
      this.#position = 0;
    }
    this.#loadTrack();
    this.#playWithFade();
  }

  #loadTrack() {
    if (!this.#audio) return;
    const url = this.#tracks[this.#order[this.#position] ?? 0];
    this.#audio.loop = this.#tracks.length === 1;
    if (url && this.#audio.getAttribute('src') !== url) this.#audio.src = url;
  }

  /** Welcome track finished → playlist; playlist track finished → next track. */
  #next() {
    if (this.#inIntro) {
      this.#startPlaylist(true);
      return;
    }
    this.#position += 1;
    if (this.#position >= this.#order.length) {
      this.#order = this.#makeOrder();
      this.#position = 0;
    }
    this.#loadTrack();
    this.#playWithFade();
  }

  #makeOrder() {
    const order = this.#tracks.map((_, i) => i);
    if (this.hasAttribute('shuffle')) {
      for (let i = order.length - 1; i > 0; i -= 1) {
        const j = Math.floor(Math.random() * (i + 1));
        [order[i], order[j]] = [order[j], order[i]];
      }
    }
    return order;
  }

  /** Starts playback from silence and slowly raises the volume. */
  #playWithFade() {
    if (!this.#audio) return Promise.reject();
    const audio = this.#audio;
    cancelAnimationFrame(this.#fadeFrame);
    audio.volume = this.#fadeMs > 0 ? 0 : this.#volume;
    const playing = audio.play();
    playing.then(() => {
      if (this.#fadeMs <= 0) return;
      const start = performance.now();
      const step = (/** @type {number} */ now) => {
        const progress = Math.min((now - start) / this.#fadeMs, 1);
        audio.volume = this.#volume * progress;
        if (progress < 1) this.#fadeFrame = requestAnimationFrame(step);
      };
      this.#fadeFrame = requestAnimationFrame(step);
    }).catch(() => {});
    return playing;
  }

  /** Music was left on: continue where it was, else wait for an interaction. */
  #resume() {
    if (!this.#audio) return;
    if (this.#inIntro) {
      const intro = this.getAttribute('intro');
      if (intro) this.#audio.src = intro;
    } else {
      if (this.#tracks.length === 0) return;
      if (this.#order.length !== this.#tracks.length) {
        this.#order = this.#makeOrder();
        this.#position = 0;
      }
      this.#loadTrack();
    }
    this.#render(true);
    this.#playWithFade().catch(() => {
      this.#render(false);
      this.#resumeAc = new AbortController();
      const start = (/** @type {Event} */ event) => {
        // Let the music button's own click decide instead of double-toggling.
        if (event.target instanceof Node && this.contains(event.target)) return;
        this.#resumeAc?.abort();
        if (this.#read(localStorage, PREF_KEY) === 'on') this.#playWithFade().catch(() => {});
      };
      const opts = { signal: this.#resumeAc.signal, capture: true };
      document.addEventListener('pointerdown', start, opts);
      document.addEventListener('keydown', start, opts);
    });
  }

  #restoreState() {
    if (!this.#audio) return;
    try {
      const state = JSON.parse(this.#read(sessionStorage, STATE_KEY) || 'null');
      if (!state) return;
      const sameList = Array.isArray(state.order) && state.order.length === this.#tracks.length;
      this.#inIntro = Boolean(state.intro);
      this.#order = sameList ? state.order : [];
      this.#position = sameList ? Math.min(Number(state.position) || 0, this.#tracks.length - 1) : 0;
      const time = Number(state.time) || 0;
      if (time > 0) {
        this.#audio.preload = 'auto';
        this.#audio.addEventListener('loadedmetadata', () => {
          if (this.#audio && time < this.#audio.duration) this.#audio.currentTime = time;
        }, { once: true });
      }
    } catch {
      // Ignore unreadable state; music starts fresh.
    }
  }

  #saveState() {
    if (!this.#audio || this.#audio.paused) return;
    this.#write(sessionStorage, STATE_KEY, JSON.stringify({
      intro: this.#inIntro,
      order: this.#order,
      position: this.#position,
      time: this.#audio.currentTime,
    }));
  }

  /** @param {boolean} playing */
  #render(playing) {
    this.#button?.setAttribute('aria-pressed', String(playing));
  }

  /** @param {Storage} store @param {string} key */
  #read(store, key) {
    try { return store.getItem(key); } catch { return null; }
  }

  /** @param {Storage} store @param {string} key @param {string} value */
  #write(store, key, value) {
    try { store.setItem(key, value); } catch { /* storage unavailable; state lasts for this page */ }
  }
}

if (!customElements.get('s-background-music')) {
  customElements.define('s-background-music', SBackgroundMusic);
}
